import * as React from 'react';
import { DiscountBadge } from './DiscountBadge';
import { SaleDiscountBadge } from './SaleDiscountBadge/SaleDiscountBadge';
import { CycleDetails, AdvancedPlansProductData } from 'types';
import { RootStore, withInjectedRootStore } from 'providers';
import { PaymentCycle, Country } from 'enums';
import { SaleBadge } from './SaleBadge';
import { Tooltip } from 'wix-style-react';
import { useTranslation } from 'react-i18next';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';

import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { getCycleKey, getSaleDiscountPercentage, isCycleOfMonths } from 'utils';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';

export interface BadgeCompProps extends InjectedExperimentsProps {
  cycle: CycleDetails;
  rootStore: RootStore;
  isCycleSelected: boolean;
}

const BadgeComp: React.FunctionComponent<BadgeCompProps> = ({
  cycle,
  isCycleSelected,
  rootStore,
  experiments,
}) => {
  const { t } = useTranslation();
  const [currencyToString] = usePremiumCurrencyFormatter();

  const {
    cycleId,
    fullTermDisplaySavings,
    monthlyFullPrice,
    fullTermActualPrice,
    firstPeriodPrice,
    isCycleDisabledInProduct,
  } = cycle;
  const {
    isInSale,
    isSaleProduct,
    appContext,
    currentProduct,
    isSecondUpgrade,
  } = rootStore.appStore;
  const { currentPlanDetails } = appContext;
  const _discountPercent = getSaleDiscountPercentage(
    experiments.all(),
    new AdvancedPlansProductModel(
      currentProduct! as AdvancedPlansProductData,
      rootStore.appStore,
    ),
  );
  const isCurrentCycleOrLowerSecondUpgrade = () =>
    isSecondUpgrade &&
    (isCycleDisabledInProduct || currentPlanDetails?.paymentCycle === cycleId);

  const isIndia = appContext.country === Country.IN;
  const shouldShowDiscountBadge =
    !isIndia &&
    !isInSale &&
    !isCycleOfMonths(cycleId) &&
    (fullTermDisplaySavings || firstPeriodPrice) &&
    !isCurrentCycleOrLowerSecondUpgrade();
  const shouldShowSaleBadge =
    (isSaleProduct || !!firstPeriodPrice) &&
    (cycleId === PaymentCycle.YEARLY ||
      cycleId === PaymentCycle.YEAR2 ||
      cycleId === PaymentCycle.YEAR3) &&
    (!isSecondUpgrade || !isCycleDisabledInProduct);

  const shouldShowBrazilSaleBadge =
    shouldShowSaleBadge && appContext.country === Country.BR;

  const cycleDiscountTooltipIsOpenedBi = () => {
    const {
      appContext: { biLogger, siteGuid },
      planType,
    } = rootStore.appStore;

    biLogger.logDiscountTooltipIsShown({
      ppType: planType,
      siteGuid,
    });
  };

  const discountBadgeTooltipContent = () =>
    t('cycle_entry.second_upgrade.save_badge_tooltip', {
      amount: currencyToString(Math.floor(fullTermDisplaySavings), {
        hideZeroDecimals: true,
      }),
      recommendedCycle: t(getCycleKey(cycleId)),
      currentCycle: t(getCycleKey(currentPlanDetails?.paymentCycle)),
    });

  const renderDiscountBadge = () => (
    <DiscountBadge
      logBi={cycleDiscountTooltipIsOpenedBi}
      cycleId={cycleId}
      discount={fullTermDisplaySavings}
      isCycleSelected={isCycleSelected}
    />
  );

  if (shouldShowDiscountBadge) {
    return isSecondUpgrade ? (
      <Tooltip
        content={discountBadgeTooltipContent()}
        maxWidth={250}
        dataHook='discount-badge-tooltip'
      >
        {renderDiscountBadge()}
      </Tooltip>
    ) : (
      renderDiscountBadge()
    );
  }
  if (shouldShowBrazilSaleBadge) {
    return <SaleBadge isCycleSelected={isCycleSelected} cycle={cycle} />;
  }
  if (shouldShowSaleBadge) {
    return (
      <>
        <SaleBadge isCycleSelected={isCycleSelected} cycle={cycle} />
        <SaleDiscountBadge
          logBi={cycleDiscountTooltipIsOpenedBi}
          discount={_discountPercent}
          cycleId={cycleId}
          monthlyFullPrice={monthlyFullPrice}
          isCycleSelected={isCycleSelected}
          fullTermActualPrice={fullTermActualPrice}
        />
      </>
    );
  }
  return null;
};

export const Badge = withInjectedRootStore(withExperiments(BadgeComp));
